import { clsx } from "clsx";
import { twMerge } from "tailwind-merge"

export function cn(...inputs) {
  return twMerge(clsx(inputs));
}

// Format seconds to HH:MM:SS
export function formatDuration(seconds) {
  if (!seconds || seconds < 0) return '00:00:00';
  
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;
  
  return [hours, minutes, secs]
    .map(v => v.toString().padStart(2, '0'))
    .join(':');
}

// Format seconds to human readable (e.g., "2h 30m")
export function formatDurationShort(seconds) {
  if (!seconds || seconds < 0) return '0m';
  
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  
  if (hours > 0) {
    return `${hours}h ${minutes}m`;
  }
  return `${minutes}m`;
}

// Format date to locale string
export function formatDate(dateString) {
  if (!dateString) return '';
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
}

// Format datetime to locale string
export function formatDateTime(dateString) {
  if (!dateString) return '';
  const date = new Date(dateString);
  return date.toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
}

// Format time only
export function formatTime(dateString) {
  if (!dateString) return '';
  const date = new Date(dateString);
  return date.toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit'
  });
}

// Get status color class
export function getStatusColor(status) {
  const colors = {
    working: 'bg-emerald-500',
    pause: 'bg-amber-500',
    lunch: 'bg-sky-500',
    outside: 'bg-slate-400',
    no_gps: 'bg-rose-500',
  };
  return colors[status] || 'bg-slate-400';
}

// Get status text color class
export function getStatusTextColor(status) {
  const colors = {
    working: 'text-emerald-500',
    pause: 'text-amber-500',
    lunch: 'text-sky-500',
    outside: 'text-slate-400',
    no_gps: 'text-rose-500',
  };
  return colors[status] || 'text-slate-400';
}

// Get status badge variant
export function getStatusBadgeClass(status) {
  const variants = {
    working: 'bg-emerald-500/10 text-emerald-500 border-emerald-500/20',
    pause: 'bg-amber-500/10 text-amber-500 border-amber-500/20',
    lunch: 'bg-sky-500/10 text-sky-500 border-sky-500/20',
    outside: 'bg-slate-500/10 text-slate-500 border-slate-500/20',
    no_gps: 'bg-rose-500/10 text-rose-500 border-rose-500/20',
  };
  return variants[status] || variants.outside;
}

// Get status label
export function getStatusLabel(status) {
  const labels = {
    working: 'Working',
    pause: 'Pause',
    lunch: 'Lunch',
    outside: 'Outside',
    no_gps: 'No GPS',
  };
  return labels[status] || status;
}

// Get role label
export function getRoleLabel(role) {
  const labels = {
    admin: 'Admin',
    hr: 'HR',
    controller: 'Controller',
    employee: 'Employee',
  };
  return labels[role] || role;
}

// Get role badge class
export function getRoleBadgeClass(role) {
  const variants = {
    admin: 'bg-purple-500/10 text-purple-500 border-purple-500/20',
    hr: 'bg-blue-500/10 text-blue-500 border-blue-500/20',
    controller: 'bg-orange-500/10 text-orange-500 border-orange-500/20',
    employee: 'bg-slate-500/10 text-slate-500 border-slate-500/20',
  };
  return variants[role] || variants.employee;
}

// Calculate percentage
export function calculatePercentage(value, total) {
  if (!total || total === 0) return 0;
  return Math.round((value / total) * 100);
}

// Get current date string YYYY-MM-DD
export function getCurrentDateString() {
  return new Date().toISOString().split('T')[0];
}

// Get month start date string
export function getMonthStartDate(date = new Date()) {
  return new Date(date.getFullYear(), date.getMonth(), 1).toISOString().split('T')[0];
}

// Get month end date string
export function getMonthEndDate(date = new Date()) {
  return new Date(date.getFullYear(), date.getMonth() + 1, 0).toISOString().split('T')[0];
}

// Debounce function
export function debounce(func, wait) {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
}
